// src/pages/TenantManagement/TenantManagement.js
import React, { useState, useEffect } from 'react';
import {
    createTenant,
    getTenants,
    updateTenant,
    getTenantUsers,
    addUserToTenant
} from '../../services/tenantService';
import './TenantManagement.css';

const TenantManagement = () => {
    const [tenants, setTenants] = useState([]);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [tenantUsers, setTenantUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Modal states
    const [isCreateTenantModalOpen, setIsCreateTenantModalOpen] = useState(false);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

    // Form states
    const [newTenant, setNewTenant] = useState({
        name: '',
        email: '',
        tenantId: ''
    });

    const [newUser, setNewUser] = useState({
        username: '',
        email: '',
        password: '',
        role: 'USER'
    });

    // Fetch tenants on component mount
    useEffect(() => {
        fetchTenants();
    }, []);

    // Fetch tenant users when a tenant is selected
    useEffect(() => {
        if (selectedTenant) {
            fetchTenantUsers(selectedTenant.tenantId);
        }
    }, [selectedTenant]);

    const fetchTenants = async () => {
        setLoading(true);
        try {
            const response = await getTenants();
            setTenants(response.data.tenants);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch tenants');
        } finally {
            setLoading(false);
        }
    };

    const fetchTenantUsers = async (tenantId) => {
        try {
            const response = await getTenantUsers(tenantId);
            setTenantUsers(response.data.users);
        } catch (err) {
            console.error('Error fetching tenant users:', err);
        }
    };

    const handleCreateTenant = async (e) => {
        e.preventDefault();
        try {
            await createTenant(newTenant);
            setIsCreateTenantModalOpen(false);
            setNewTenant({ name: '', email: '', tenantId: '' });
            fetchTenants();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create tenant');
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        if (!selectedTenant) return;

        try {
            await addUserToTenant(selectedTenant.tenantId, newUser);
            setIsAddUserModalOpen(false);
            setNewUser({ username: '', email: '', password: '', role: 'USER' });
            fetchTenantUsers(selectedTenant.tenantId);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to add user');
        }
    };

    const handleTenantActivation = async (tenant, isActive) => {
        try {
            await updateTenant(tenant.id, { ...tenant, isActive });
            fetchTenants();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update tenant');
        }
    };

    return (
        <div className="tenant-management-container">
            <div className="tenant-management-header">
                <h1>Tenant Management</h1>
                <button
                    className="create-tenant-btn"
                    onClick={() => setIsCreateTenantModalOpen(true)}
                >
                    Create New Tenant
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="tenant-management-content">
                <div className="tenants-list">
                    <h2>Tenants</h2>
                    {loading ? (
                        <p>Loading tenants...</p>
                    ) : (
                        <table className="tenants-table">
                            <thead>
                            <tr>
                                <th>Tenant ID</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {tenants.map((tenant) => (
                                <tr
                                    key={tenant.id}
                                    className={selectedTenant?.id === tenant.id ? 'selected' : ''}
                                    onClick={() => setSelectedTenant(tenant)}
                                >
                                    <td>{tenant.tenantId}</td>
                                    <td>{tenant.name}</td>
                                    <td>{tenant.email}</td>
                                    <td>
                                            <span className={`status-badge ${tenant.isActive ? 'active' : 'inactive'}`}>
                                                {tenant.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                    </td>
                                    <td>
                                        <button
                                            className="action-btn"
                                            onClick={() => handleTenantActivation(tenant, !tenant.isActive)}
                                        >
                                            {tenant.isActive ? 'Deactivate' : 'Activate'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {selectedTenant && (
                    <div className="tenant-users">
                        <div className="tenant-users-header">
                            <h2>Users for {selectedTenant.name}</h2>
                            <button
                                className="add-user-btn"
                                onClick={() => setIsAddUserModalOpen(true)}
                            >
                                Add User
                            </button>
                        </div>
                        <table className="users-table">
                            <thead>
                            <tr>
                                <th>Username</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Status</th>
                            </tr>
                            </thead>
                            <tbody>
                            {tenantUsers.map((user) => (
                                <tr key={user.id}>
                                    <td>{user.username}</td>
                                    <td>{user.email}</td>
                                    <td>{user.role}</td>
                                    <td>
                                            <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                                                {user.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Create Tenant Modal */}
            {isCreateTenantModalOpen && (
                <div className="modal">
                    <div className="modal-content">
                        <h2>Create New Tenant</h2>
                        <form onSubmit={handleCreateTenant}>
                            <div className="form-group">
                                <label>Tenant ID:</label>
                                <input
                                    type="text"
                                    value={newTenant.tenantId}
                                    onChange={(e) => setNewTenant({
                                        ...newTenant,
                                        tenantId: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Name:</label>
                                <input
                                    type="text"
                                    value={newTenant.name}
                                    onChange={(e) => setNewTenant({
                                        ...newTenant,
                                        name: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Email:</label>
                                <input
                                    type="email"
                                    value={newTenant.email}
                                    onChange={(e) => setNewTenant({
                                        ...newTenant,
                                        email: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="modal-actions">
                                <button type="button" onClick={() => setIsCreateTenantModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit">Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add User Modal */}
            {isAddUserModalOpen && (
                <div className="modal">
                    <div className="modal-content">
                        <h2>Add User to {selectedTenant.name}</h2>
                        <form onSubmit={handleAddUser}>
                            <div className="form-group">
                                <label>Username:</label>
                                <input
                                    type="text"
                                    value={newUser.username}
                                    onChange={(e) => setNewUser({
                                        ...newUser,
                                        username: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Email:</label>
                                <input
                                    type="email"
                                    value={newUser.email}
                                    onChange={(e) => setNewUser({
                                        ...newUser,
                                        email: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Password:</label>
                                <input
                                    type="password"
                                    value={newUser.password}
                                    onChange={(e) => setNewUser({
                                        ...newUser,
                                        password: e.target.value
                                    })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Role:</label>
                                <select
                                    value={newUser.role}
                                    onChange={(e) => setNewUser({
                                        ...newUser,
                                        role: e.target.value
                                    })}
                                >
                                    <option value="USER">User</option>
                                    <option value="TENANT_ADMIN">Tenant Admin</option>
                                </select>
                            </div>
                            <div className="modal-actions">
                                <button type="button" onClick={() => setIsAddUserModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit">Add User</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TenantManagement;